import { DEAL_FIELDS, PROJECT_FIELDS } from './boards.js';
import { CANDIDATE_FIELDS } from './candidates.js';
import { JOB_FIELDS } from './jobs-records.js';
/**
 * Merge fields for pipeline trigger actions: `{{entity.field}}` in a task title, a
 * description or a note body, filled from a bundle of plain values the worker reads for the run.
 * Custom fields are `{{candidate.custom.<key>}}`. No logic, loops or filters: a template is
 * text with holes, so it cannot run anything.
 */
export const TEMPLATE_ENTITIES = [
    'candidate',
    'deal',
    'job',
    'project',
    'company',
    'contact',
];
/**
 * The fields a template may name, per entity: the native keys of each entity's FIELDS map.
 * Companies and contacts have no filter catalogue yet, so their list is spelled out here.
 * Custom fields (`custom.<key>`) are checked against the organisation's definitions, not here.
 */
export const TEMPLATE_FIELDS = {
    // Sensitive until the compliance decision: never sent anywhere a template's output goes.
    candidate: Object.keys(CANDIDATE_FIELDS).filter((k) => k !== 'dateOfBirth' && k !== 'gender'),
    deal: Object.keys(DEAL_FIELDS),
    job: Object.keys(JOB_FIELDS),
    project: Object.keys(PROJECT_FIELDS),
    company: ['id', 'name', 'domain', 'website', 'industry', 'country', 'phone', 'ownerId'],
    contact: [
        'id',
        'firstName',
        'lastName',
        'email',
        'phone',
        'altPhone',
        'jobTitle',
        'companyId',
        'ownerId',
    ],
};
/**
 * The entities a trigger's templates can name, by its subject: what the worker loads for a run
 * (the subject, its job, project, company and the project's primary contact). A Candidate Flow
 * candidate belongs to no project.
 */
export const TEMPLATE_ENTITIES_BY_SUBJECT = {
    project: ['project', 'company', 'contact'],
    deal: ['deal', 'project', 'company', 'contact'],
    job: ['job', 'project', 'company', 'contact'],
    candidate: ['candidate'],
    shortlist: ['candidate', 'job', 'project', 'company', 'contact'],
};
const PLACEHOLDER = /\{\{\s*([a-zA-Z][\w]*(?:\.[a-zA-Z_][\w-]*){1,2})\s*\}\}/g;
/** Every distinct `entity.field` (or `entity.custom.key`) a template names, in order of use. */
export function templatePlaceholders(template) {
    return [...new Set([...template.matchAll(PLACEHOLDER)].map((m) => m[1]))];
}
/** The placeholders a template names that are not an entity's native field (custom ones are kept). */
export function unknownTemplatePlaceholders(template) {
    return templatePlaceholders(template).filter((p) => {
        const [entity, field, key] = p.split('.');
        if (!TEMPLATE_ENTITIES.includes(entity))
            return true;
        if (field === 'custom')
            return entity !== 'candidate' || !key;
        return key !== undefined || !TEMPLATE_FIELDS[entity].includes(field);
    });
}
function text(value) {
    if (value === null || value === undefined)
        return '';
    if (Array.isArray(value))
        return value.map(text).join(', ');
    if (value instanceof Date)
        return value.toISOString();
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number' || typeof value === 'boolean')
        return String(value);
    return JSON.stringify(value) ?? '';
}
/**
 * Fill a template. A placeholder the bundle cannot answer renders empty and is reported in
 * `missing`; an empty value that exists (null, '') is not missing.
 */
/** Backslash-escapes what would turn text into a link, image, HTML or emphasis in Markdown. */
const escapeMarkdown = (s) => s.replace(/[\\`*_[\]<>]/g, '\\$&');
/**
 * `markdown`: the output is rendered as Markdown (a note, a task description), so each value is
 * escaped: record data stays text, and only the template's own formatting formats.
 */
export function renderTemplate(template, bundle, opts = {}) {
    const missing = new Set();
    const out = template.replace(PLACEHOLDER, (_, path) => {
        const [entity, field, key] = path.split('.');
        const record = bundle[entity];
        const source = (key === undefined ? record : field === 'custom' ? record?.custom : undefined);
        const name = key ?? field;
        if (!source || !Object.prototype.hasOwnProperty.call(source, name)) {
            missing.add(path);
            return '';
        }
        const value = text(source[name]);
        return opts.markdown ? escapeMarkdown(value) : value;
    });
    return { text: out, missing: [...missing] };
}
//# sourceMappingURL=templates.js.map